import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    BooleTypes,
    ExhibitTypes,
    FurnitureTypes,
    LightsTypes,
    PartitionTypes,
    ReceptionTypes,
    TVTypes,
} from './current-model-state';

type ModelProperty<
    T,
    S extends
        | 'reception'
        | 'tvFirst'
        | 'tvSecond'
        | 'exhibitSecond'
        | 'exhibit'
        | 'lightsFirst'
        | 'lightsSecond'
        | 'furnitureFirst'
        | 'furnitureSecond'
        | 'boole'
        | 'partition'
        | 'color',
    W extends 'select' | 'multiselect' | 'color' = 'select',
> = {
    type: W;
    title: string;
    slug: S;
    property: T;
    cameraPosition?: [number, number, number, number, number, number];
};

type GroupProperty<
    T,
    S extends 'tvFirst' | 'tvSecond' | 'lightsFirst' | 'lightsSecond' | 'furnitureFirst' | 'furnitureSecond',
> = {
    title: string;
    slug: S;
    property: T;
    cameraPosition?: [number, number, number, number, number, number];
};

type PropertyColor = ModelProperty<
    {
        label: string;
        value: string | null;
        slug: 'color';
        cameraPosition?: [number, number, number, number, number, number];
    }[],
    'color',
    'color'
>;

type PropertyReception = ModelProperty<
    {
        label: string;
        slug: ReceptionTypes;
        hint?: string;
    }[],
    'reception'
>;

type PropertyLightsFirst = GroupProperty<
    {
        label: string;
        slug: LightsTypes;
        hint?: string;
    }[],
    'lightsFirst'
>;

type PropertyLightsSecond = GroupProperty<
    {
        label: string;
        slug: LightsTypes;
        hint?: string;
    }[],
    'lightsSecond'
>;

type PropertyTvFirst = GroupProperty<
    {
        label: string;
        slug: TVTypes;
        hint?: string;
    }[],
    'tvFirst'
>;

type PropertyTvSecond = GroupProperty<
    {
        label: string;
        slug: TVTypes;
        hint?: string;
    }[],
    'tvSecond'
>;

type PropertyFurnitureFirst = GroupProperty<
    {
        label: string;
        slug: FurnitureTypes;
        hint?: string;
    }[],
    'furnitureFirst'
>;

type PropertyFurnitureSecond = GroupProperty<
    {
        label: string;
        slug: FurnitureTypes;
        hint?: string;
    }[],
    'furnitureSecond'
>;

type PropertyExhibit = ModelProperty<
    {
        label: string;
        slug: ExhibitTypes;
        hint?: string;
    }[],
    'exhibit'
>;

type PropertyExhibitSecond = ModelProperty<
    {
        label: string;
        slug: ExhibitTypes;
        hint?: string;
    }[],
    'exhibitSecond'
>;

type PropertyPartition = ModelProperty<
    {
        label: string;
        slug: PartitionTypes;
        hint?: string;
    }[],
    'partition',
    'select'
>;

type PropertyBoole = ModelProperty<
    {
        label: string;
        slug: BooleTypes;
        hint?: string;
    }[],
    'boole',
    'multiselect'
>;

type PropertyGroup = {
    title: string;
    values: (
        | PropertyLightsFirst
        | PropertyTvFirst
        | PropertyFurnitureFirst
        | PropertyLightsSecond
        | PropertyTvSecond
        | PropertyFurnitureSecond
    )[];
    cameraPosition?: [number, number, number, number, number, number];
    type: 'group';
};

export type ModelProperties =
    | PropertyColor
    | PropertyReception
    | PropertyExhibit
    | PropertyExhibitSecond
    | PropertyBoole
    | PropertyPartition
    | PropertyGroup;

const currentModelPropertiesState = atom<ModelProperties[]>({
    key: 'currentModelPropertiesState',
    default: [],
});

export const useCurrentModelPropertiesValue = () => useRecoilValue(currentModelPropertiesState);

export const useSetCurrentModelPropertiesValue = () => useSetRecoilState(currentModelPropertiesState);
